import { Injectable } from '@angular/core';
import { datadogLogs } from '@datadog/browser-logs';
import { environment } from '@env/environment';
import { Logger, LogLevel, LogOutput } from './logger.service';

const REDACTED = '[REDACTED]';

/**
 * Substrings (case-insensitive) that mark a field name as sensitive. Any object
 * key containing one of these has its value replaced with `[REDACTED]` before
 * the event is sent to Datadog. Matched as substrings so e.g. `userPassword`,
 * `X-Auth-Token`, and `accessToken` are all caught.
 */
const SENSITIVE_KEY_PARTS = [
  'password',
  'token',
  'authorization',
  'auth',
  'secret',
  'apikey',
  'jwt',
  'cookie',
  'session',
  'email',
  'ssn',
  'creditcard',
];

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_KEY_PARTS.some((part) => lower.includes(part));
}

/**
 * Recursively redacts values under sensitive keys in `value`, mutating objects
 * and arrays in place and returning the same reference. Primitives, null, and
 * undefined pass through unchanged. Guards against circular references and
 * caps recursion depth so a pathological object cannot hang the browser.
 */
export function redactSensitive(value: unknown, seen = new WeakSet<object>(), depth = 0): unknown {
  if (value === null || typeof value !== 'object' || depth > 10) {
    return value;
  }
  const obj = value as object;
  if (seen.has(obj)) {
    return value;
  }
  seen.add(obj);

  if (Array.isArray(value)) {
    value.forEach((item, i) => {
      value[i] = redactSensitive(item, seen, depth + 1);
    });
    return value;
  }

  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (isSensitiveKey(key)) {
      record[key] = REDACTED;
    } else {
      record[key] = redactSensitive(record[key], seen, depth + 1);
    }
  }
  return value;
}

/**
 * Redacts sensitive attributes of an outgoing Datadog event while keeping the
 * `usr` block intact. `usr` is the identity attached deliberately via
 * `setUser()` (id, email, name) so log lines can be attributed to the signed-in
 * administrator; the substring denylist would otherwise blank out `usr.email`.
 */
export function redactSensitiveExceptUser(event: unknown): unknown {
  if (event === null || typeof event !== 'object') {
    return event;
  }
  const record = event as Record<string, unknown>;
  const usr = record['usr'];
  delete record['usr'];
  redactSensitive(record);
  if (usr !== undefined) {
    record['usr'] = usr;
  }
  return event;
}

/**
 * Initializes Datadog Browser Logs and forwards every Logger call to Datadog.
 *
 * Active only when `environment.datadog.clientToken` is non-empty, so local DEV
 * (empty token) stays inactive. Registers a single LogOutput into the existing
 * `Logger.outputs` hook; `logger.service.ts` itself is unchanged. A `beforeSend`
 * hook redacts sensitive fields on every event — including the errors and
 * network logs captured automatically via `forwardErrorsToLogs` — before they
 * leave the browser.
 */
@Injectable({ providedIn: 'root' })
export class DatadogLoggerService {
  private initialized = false;

  init(): void {
    if (this.initialized) {
      return;
    }

    const config = environment.datadog;
    if (!config?.clientToken) {
      return;
    }

    try {
      datadogLogs.init({
        clientToken: config.clientToken,
        site: config.site,
        service: config.service,
        env: config.env,
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
        forwardConsoleLogs: [],
        beforeSend: (event: unknown) => {
          // Redact sensitive fields on every outgoing event, including errors
          // and network logs captured via forwardErrorsToLogs. The message
          // string is left intact; only object attributes are scrubbed.
          redactSensitiveExceptUser(event);
          return true;
        },
      });

      Logger.outputs.push(this.createOutput());
      this.initialized = true;
    } catch (error) {
      // Never let a logging-init failure break app bootstrap.
      // eslint-disable-next-line no-console
      console.error('[DatadogLoggerService] init failed', error);
    }
  }

  private createOutput(): LogOutput {
    return (source: string | undefined, level: LogLevel, ...objects: unknown[]) => {
      const [first, ...rest] = objects;
      const message = typeof first === 'string' ? first : source || 'log';
      const context = typeof first === 'string' ? rest : objects;
      const attributes = { source, context };

      switch (level) {
        case LogLevel.Debug:
          datadogLogs.logger.debug(message, attributes);
          break;
        case LogLevel.Info:
          datadogLogs.logger.info(message, attributes);
          break;
        case LogLevel.Warning:
          datadogLogs.logger.warn(message, attributes);
          break;
        case LogLevel.Error:
          datadogLogs.logger.error(message, attributes);
          break;
        default:
          break;
      }
    };
  }
}
